'use client';


import { withRef } from '@udecode/cn';
import { useEditorPlugin, useEditorRef } from '@udecode/plate/react';
import { Editor } from 'slate';

import { CUSTOM_AI_PLUGIN_KEY } from '@/components/editor/plugins/custom-ai-plugin';
import { useLicense } from '@/hooks/useLicense';
import { ToolbarButton } from './toolbar';

export const AIToolbarButton = withRef<typeof ToolbarButton>(
  ({ children, ...rest }, ref) => {
    const editor = useEditorRef();
    const { hasAIFeatures } = useLicense();

    // Don't render if user doesn't have AI features
    if (!hasAIFeatures) {
      return null;
    }

    // Get the CustomAIPlugin API
    let customAIApi = null;
    try {
      const { setOptions } = useEditorPlugin({ key: CUSTOM_AI_PLUGIN_KEY });
      customAIApi = setOptions;
    } catch (error) {
      console.log('CustomAIPlugin not available:', error);
    }

    const handleClick = () => {
      console.log('AI Toolbar Button clicked');
      console.log('CustomAI API:', customAIApi);

      // Use Custom AI
      if (customAIApi) {
        try {
          // Get current selection or cursor position
          const selection = editor.selection;
          let anchorElement: HTMLElement | null = null;

          if (selection) {
            try {
              if (editor.api.isExpanded(selection)) {
                // Text is selected - get the last selected block
                const selectedBlocks = editor.api.blocks();
                if (selectedBlocks.length > 0) {
                  const lastBlock = selectedBlocks.at(-1)![0];
                  anchorElement = editor.api.toDOMNode(lastBlock);
                }
              } else {
                // Just cursor position - get current block
                const [currentBlock] = editor.api.block({ highest: true }) || [];
                if (currentBlock) {
                  anchorElement = editor.api.toDOMNode(currentBlock);
                }
              }
            } catch (error) {
              console.warn('Could not get DOM node for selection:', error);
            }
          }

          // Fallback: get the editor container
          if (!anchorElement) {
            const editorElement = editor.api.toDOMNode(editor);
            if (editorElement) {
              const firstBlock = editorElement.querySelector('[data-slate-node="element"]');
              anchorElement = firstBlock as HTMLElement || editorElement;
            }
          }

          // Final fallback: use document.body if we still don't have an anchor
          if (!anchorElement) {
            anchorElement = document.body;
          }

          if (anchorElement) {
            // Get selected text
            let selectedText = '';
            if (selection && editor.api.isExpanded(selection)) {
              try {
                selectedText = Editor.string(editor, selection);
              } catch (error) {
                console.warn('Could not get selected text:', error);
                selectedText = '';
              }
            }

            customAIApi({
              isOpen: true,
              anchorElement,
              selectedText,
              action: null,
              isLoading: false,
            });
            return;
          }
        } catch (error) {
          console.error('Error calling Custom AI:', error);
        }
      } else {
        console.error('Custom AI not available');
      }
    };

    return (
      <ToolbarButton
        ref={ref}
        {...rest}
        onClick={handleClick}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        data-plate-focus
      >
        {children}
      </ToolbarButton>
    );
  }
);
